import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { 
  MaquinaParaDragDrop, 
  UsuarioConMaquinas, 
  RespuestaDatosDragDrop 
} from '../../../services/asignacionesUsuarioMaquinaService';

// ===============================
// TIPOS PARA EL ESTADO
// ===============================

export interface DragDropState {
  // Datos principales
  usuariosConMaquinas: UsuarioConMaquinas[];
  maquinasDisponibles: MaquinaParaDragDrop[];
  organizacionId: number;
  
  // Estado de la UI
  isLoading: boolean;
  isDragging: boolean;
  hasChanges: boolean;
  
  // Estadísticas
  totalMaquinas: number;
  maquinasAsignadas: number;
  totalUsuarios: number;
  
  // Estados de operación
  isSaving: boolean;
  saveError: string | null;
  lastSaved: Date | null;
}

export type DragDropAction =
  | { type: 'SET_INITIAL_DATA'; payload: RespuestaDatosDragDrop }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_DRAGGING'; payload: boolean }
  | { type: 'UPDATE_AVAILABLE_MACHINES'; payload: MaquinaParaDragDrop[] }
  | { type: 'MOVE_MACHINE_TO_USER'; payload: { maquinaId: number; usuarioId: number } }
  | { type: 'MOVE_MACHINE_TO_AVAILABLE'; payload: { maquinaId: number; fromUserId: number } }
  | { type: 'MOVE_MACHINE_BETWEEN_USERS'; payload: { maquinaId: number; fromUserId: number; toUserId: number } }
  | { type: 'SET_SAVING'; payload: boolean }
  | { type: 'SET_SAVE_ERROR'; payload: string | null }
  | { type: 'MARK_SAVED'; payload: Date }
  | { type: 'RESET_CHANGES' };

// ===============================
// ESTADO INICIAL
// ===============================

const initialState: DragDropState = {
  usuariosConMaquinas: [],
  maquinasDisponibles: [],
  organizacionId: 0,
  isLoading: true,
  isDragging: false,
  hasChanges: false,
  totalMaquinas: 0,
  maquinasAsignadas: 0,
  totalUsuarios: 0,
  isSaving: false,
  saveError: null,
  lastSaved: null,
};

// ===============================
// REDUCER
// ===============================

function dragDropReducer(state: DragDropState, action: DragDropAction): DragDropState {
  switch (action.type) {
    case 'SET_INITIAL_DATA':
      return {
        ...state,
        usuariosConMaquinas: action.payload.usuarios_con_maquinas,
        maquinasDisponibles: action.payload.maquinas_disponibles,
        organizacionId: action.payload.organizacion_id,
        totalMaquinas: action.payload.total_maquinas,
        maquinasAsignadas: action.payload.maquinas_asignadas,
        totalUsuarios: action.payload.total_usuarios,
        isLoading: false,
        hasChanges: false,
      };

    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_DRAGGING':
      return { ...state, isDragging: action.payload };

    case 'UPDATE_AVAILABLE_MACHINES':
      return { ...state, maquinasDisponibles: action.payload };

    case 'MOVE_MACHINE_TO_USER': {
      const { maquinaId, usuarioId } = action.payload;
      
      // Encontrar la máquina en disponibles
      const maquina = state.maquinasDisponibles.find(m => m.id === maquinaId);
      if (!maquina) return state;

      // Remover de disponibles
      const nuevasMaquinasDisponibles = state.maquinasDisponibles.filter(m => m.id !== maquinaId);

      // Agregar al usuario (crear usuario si no existe)
      let nuevosUsuarios = [...state.usuariosConMaquinas];
      const usuarioIndex = nuevosUsuarios.findIndex(u => u.usuario_uau_id === usuarioId);

      if (usuarioIndex >= 0) {
        // Usuario existe, agregar máquina
        nuevosUsuarios[usuarioIndex] = {
          ...nuevosUsuarios[usuarioIndex],
          maquinas: [...nuevosUsuarios[usuarioIndex].maquinas, maquina]
        };
      } else {
        // Usuario no existe, crear nuevo
        nuevosUsuarios.push({
          usuario_uau_id: usuarioId,
          maquinas: [maquina]
        });
      }

      return {
        ...state,
        usuariosConMaquinas: nuevosUsuarios,
        maquinasDisponibles: nuevasMaquinasDisponibles,
        maquinasAsignadas: state.maquinasAsignadas + 1,
        hasChanges: true,
      };
    }

    case 'MOVE_MACHINE_TO_AVAILABLE': {
      const { maquinaId, fromUserId } = action.payload;
      
      // Encontrar usuario y máquina
      const usuario = state.usuariosConMaquinas.find(u => u.usuario_uau_id === fromUserId);
      const maquina = usuario?.maquinas.find(m => m.id === maquinaId);
      if (!maquina) return state;

      // Remover máquina del usuario
      let nuevosUsuarios = state.usuariosConMaquinas.map(u => {
        if (u.usuario_uau_id === fromUserId) {
          const nuevasMaquinas = u.maquinas.filter(m => m.id !== maquinaId);
          return { ...u, maquinas: nuevasMaquinas };
        }
        return u;
      }).filter(u => u.maquinas.length > 0); // Remover usuarios sin máquinas

      // Agregar a disponibles
      const nuevasMaquinasDisponibles = [...state.maquinasDisponibles, maquina];

      return {
        ...state,
        usuariosConMaquinas: nuevosUsuarios,
        maquinasDisponibles: nuevasMaquinasDisponibles,
        maquinasAsignadas: state.maquinasAsignadas - 1,
        totalUsuarios: nuevosUsuarios.length,
        hasChanges: true,
      };
    }

    case 'MOVE_MACHINE_BETWEEN_USERS': {
      const { maquinaId, fromUserId, toUserId } = action.payload;
      
      // Encontrar la máquina en el usuario origen
      const fromUser = state.usuariosConMaquinas.find(u => u.usuario_uau_id === fromUserId);
      const maquina = fromUser?.maquinas.find(m => m.id === maquinaId);
      if (!maquina) return state;

      let nuevosUsuarios = [...state.usuariosConMaquinas];

      // Remover del usuario origen
      const fromIndex = nuevosUsuarios.findIndex(u => u.usuario_uau_id === fromUserId);
      if (fromIndex >= 0) {
        nuevosUsuarios[fromIndex] = {
          ...nuevosUsuarios[fromIndex],
          maquinas: nuevosUsuarios[fromIndex].maquinas.filter(m => m.id !== maquinaId)
        };
      }

      // Agregar al usuario destino
      const toIndex = nuevosUsuarios.findIndex(u => u.usuario_uau_id === toUserId);
      if (toIndex >= 0) {
        nuevosUsuarios[toIndex] = {
          ...nuevosUsuarios[toIndex],
          maquinas: [...nuevosUsuarios[toIndex].maquinas, maquina]
        };
      } else {
        // Usuario destino no existe, crear nuevo
        nuevosUsuarios.push({
          usuario_uau_id: toUserId,
          maquinas: [maquina]
        });
      }

      // Filtrar usuarios sin máquinas
      nuevosUsuarios = nuevosUsuarios.filter(u => u.maquinas.length > 0);

      return {
        ...state,
        usuariosConMaquinas: nuevosUsuarios,
        totalUsuarios: nuevosUsuarios.length,
        hasChanges: true,
      };
    }

    case 'SET_SAVING':
      return { ...state, isSaving: action.payload, saveError: null };

    case 'SET_SAVE_ERROR':
      return { ...state, saveError: action.payload, isSaving: false };

    case 'MARK_SAVED':
      return { 
        ...state, 
        hasChanges: false, 
        isSaving: false, 
        saveError: null, 
        lastSaved: action.payload 
      };

    case 'RESET_CHANGES':
      return { ...state, hasChanges: false, saveError: null };

    default:
      return state;
  }
}

// ===============================
// CONTEXTO
// ===============================

interface DragDropContextType {
  state: DragDropState;
  dispatch: React.Dispatch<DragDropAction>;
  
  // Acciones auxiliares
  moveMachineToUser: (maquinaId: number, usuarioId: number) => void;
  moveMachineToAvailable: (maquinaId: number, fromUserId: number) => void;
  moveMachineBetweenUsers: (maquinaId: number, fromUserId: number, toUserId: number) => void;
  
  // Utilidades
  getMachineById: (id: number) => MaquinaParaDragDrop | undefined;
  getUserMachines: (usuarioId: number) => MaquinaParaDragDrop[];
  hasUserMachine: (usuarioId: number, maquinaId: number) => boolean;
}

const DragDropContext = createContext<DragDropContextType | undefined>(undefined);

// ===============================
// PROVIDER
// ===============================

interface DragDropProviderProps {
  children: ReactNode;
}

export function DragDropProvider({ children }: DragDropProviderProps) {
  const [state, dispatch] = useReducer(dragDropReducer, initialState);

  // Acciones auxiliares
  const moveMachineToUser = (maquinaId: number, usuarioId: number) => {
    dispatch({ type: 'MOVE_MACHINE_TO_USER', payload: { maquinaId, usuarioId } });
  };

  const moveMachineToAvailable = (maquinaId: number, fromUserId: number) => {
    dispatch({ type: 'MOVE_MACHINE_TO_AVAILABLE', payload: { maquinaId, fromUserId } });
  };

  const moveMachineBetweenUsers = (maquinaId: number, fromUserId: number, toUserId: number) => {
    dispatch({ type: 'MOVE_MACHINE_BETWEEN_USERS', payload: { maquinaId, fromUserId, toUserId } });
  };

  // Utilidades
  const getMachineById = (id: number): MaquinaParaDragDrop | undefined => {
    // Buscar en disponibles
    const disponible = state.maquinasDisponibles.find(m => m.id === id);
    if (disponible) return disponible;

    // Buscar en asignadas
    for (const usuario of state.usuariosConMaquinas) {
      const maquina = usuario.maquinas.find(m => m.id === id);
      if (maquina) return maquina;
    }

    return undefined;
  };

  const getUserMachines = (usuarioId: number): MaquinaParaDragDrop[] => {
    const usuario = state.usuariosConMaquinas.find(u => u.usuario_uau_id === usuarioId);
    return usuario?.maquinas || [];
  };

  const hasUserMachine = (usuarioId: number, maquinaId: number): boolean => {
    const usuario = state.usuariosConMaquinas.find(u => u.usuario_uau_id === usuarioId);
    return usuario?.maquinas.some(m => m.id === maquinaId) || false;
  };

  const contextValue: DragDropContextType = {
    state,
    dispatch,
    moveMachineToUser,
    moveMachineToAvailable,
    moveMachineBetweenUsers,
    getMachineById,
    getUserMachines,
    hasUserMachine,
  };

  return (
    <DragDropContext.Provider value={contextValue}>
      {children}
    </DragDropContext.Provider>
  );
}

// ===============================
// HOOK
// ===============================

export function useDragDrop() {
  const context = useContext(DragDropContext);
  if (context === undefined) {
    throw new Error('useDragDrop debe usarse dentro de un DragDropProvider');
  }
  return context;
}
